<template>
  <CDialog
    :visible.sync="currentVisible"
    width="480px"
    :title="$t('Report bulk download')"
    submittable
    :submit-label="$t('Bulk download')"
    :submit-disabled="isDisabled"
    @submit="handleDownloadCsv"
    @open="open"
  >
    <p>
      {{
        $t(
          'You can download whole account report in one csv. Select the month and click "Bulk download".'
        )
      }}
    </p>

    <CSelector
      v-model="selectedMonth"
      :options="options"
      emit-value
      map-options
      :use-input="false"
      :error-message="errorMsg"
      :loading="loading"
    />
  </CDialog>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import { saveAs } from 'file-saver';
import CDialog from '@/components/uikit/CDialog';
import CSelector from '@/components/uikit/CSelector';
import CButton from '@/components/uikit/CButton';
import { hash } from '@/vuex/modules/billingReports';

const messages = {
  en: {
    'Report bulk download': 'Download All Reports Data',
    'You can download whole account report in one csv. Select the month and click "Bulk download".':
      'You can download reports data for all accounts into one CSV file.',
    'No existing data.': 'No existing data.',
    'Select the month': 'Select month',
    'Bulk download': 'Download',
  },
  ja: {
    'Report bulk download': 'レポート一括ダウンロード',
    'You can download whole account report in one csv. Select the month and click "Bulk download".':
      'すべてのアカウントのレポートを一括でダウンロードできます。ダウンロードする月を選択して『一括ダウンロード』をクリックしてください。',
    'Select the month': '月を選択してください',
    'Bulk download': '一括ダウンロード',
  },
};

export default {
  name: 'DialogDownloadBulkCsv',
  i18n: { messages },
  components: { CDialog, CSelector, CButton },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedMonth: '',
      errorMsg: null,
      loading: false,
    };
  },
  watch: {
    selectedMonth(value) {
      this.errorMsg = null;

      if (!value) return false;

      this.fetchCsvLinks();
    },
  },
  computed: {
    ...mapState('accounts', {
      downloadUrl: state => state.bulkCsv,
    }),

    ...mapState({
      vendor: 'vendor',
      companyId: 'companyId',
    }),

    ...mapGetters(['monthOptions']),

    ...mapGetters('user', {
      reportCostType: 'getReportCostType',
    }),

    ...mapState('billingReports', {
      reports: state => state.reports,
    }),

    usageReport() {
      return {
        owner: 'company',
        resourceType: 'account',
        vendor: 'aws',
        dimension: 'monthly',
        type: this.reportCostType,
        from: moment(this.selectedMonth, 'YYYY-MM').startOf('month'),
        to: moment(this.selectedMonth, 'YYYY-MM').endOf('month'),
      };
    },

    reportHash() {
      return hash(this.usageReport);
    },

    reportData() {
      const reportData = this.reports[this.reportHash];
      if (reportData) {
        return reportData;
      }
      return [];
    },

    options() {
      const defaultOption = { label: this.$t('Select the month'), value: '' };
      let options = [defaultOption, ...this.monthOptions(this.vendor === 'azure')];

      return options;
    },

    isDisabled() {
      return this.vendor === 'aws'
        ? !this.selectedMonth || !this.reportData.length
        : !this.selectedMonth || !this.downloadUrl;
    },

    currentVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  methods: {
    ...mapMutations('accounts', ['updateBulkCsv']),

    ...mapActions('accounts', {
      fetchReportBulkCsv: 'fetchReportBulkCsv',
      fetchAzureReportBulkCsv: 'azure/fetchReportBulkCsv',
    }),

    ...mapActions('billingReports', {
      fetchReports: 'fetchReports',
    }),

    open() {
      this.updateBulkCsv('');
      this.selectedMonth = '';
      this.loading = false;
    },

    async fetchCsvLinks() {
      try {
        // Log
        this.$alpha.logger.addBreadcrumb('Request Bulk CSV.');

        // Request start
        this.loading = true;

        switch (this.vendor) {
          case 'azure':
            await this.fetchAzureReportBulkCsv(this.selectedMonth);
            break;
          case 'gcp':
            await this.fetchReportBulkCsv(this.selectedMonth);
            break;
          case 'aws':
            await this.fetchReports(this.usageReport);
            break;
        }
      } catch (e) {
        if (e === 'No data') {
          this.errorMsg = this.$t('No existing data.');
        }

        await this.$alpha.notify.error('fetch');
      } finally {
        this.loading = false;
      }
    },

    handleDownloadCsv() {
      if (this.vendor !== 'aws') {
        window.location = this.downloadUrl;
        this.currentVisible = false;
        return;
      }

      // Generate the CSV data.
      if (this.reportData.length > 0) {
        let csvHeader = [];
        let csvData = [];
        let fileName = '';

        csvHeader = [
          'AccountId',
          'ServiceCode',
          'CostBeforeTax',
          'Region',
          'UsageQuantity',
          'InstanceType',
          'Operation',
          'ItemDescription',
        ];
        fileName = `${this.companyId}-${this.selectedMonth}-report.csv`;
        for (const item of this.reportData) {
          const serviceCode = item.service;
          for (const report of item.report) {
            csvData.push([
              report.account,
              serviceCode,
              report.cost,
              report.region,
              report.usage_quantity,
              report.instance_type,
              report.operation,
              report.description,
            ]);
          }
        }

        // Convert CSV data to String.
        var csvText = '';
        csvText += this.objectToCSVRow(csvHeader);
        for (const item of csvData) {
          csvText += this.objectToCSVRow(item);
        }

        // Save file to local machine.
        var blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, fileName, { autoBom: true });
      }
    },

    objectToCSVRow(dataObject) {
      var dataArray = new Array();
      for (var key in dataObject) {
        var innerValue = dataObject[key] === null ? '' : dataObject[key].toString();
        var result = innerValue.replace(/"/g, '""');
        result = '"' + result + '"';
        dataArray.push(result);
      }
      return dataArray.join(',') + '\r\n';
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-body {
  position: relative;
  padding: 15px;

  .download-area {
    .msg {
      position: relative;
      height: 1rem;
      span {
        position: absolute;
      }
    }

    .download-btn {
      padding-top: 1.5em;
      text-align: right;
      a:visited {
        text-decoration: none;
      }

      span {
        color: $mute-color;
        font-weight: 600;
        pointer-events: none;
        transition: $default-transition;
        i {
          margin-right: 0.5em;
        }

        &.active {
          color: $primary-color;
          cursor: pointer;
          pointer-events: visible;
          &:hover {
            color: $primary-color-lighten;
          }
        }
      }
    }
  }
}
</style>
